import type { OfflineDebugState, IOfflineDetector } from './types';

type ManagedListener = {
  readonly type: string;
  readonly handler: EventListener;
};

export class OfflineDetector implements IOfflineDetector {
  private online = true;

  private paused = false;

  private simulatedOffline = false;

  private onOnline: (() => void) | null = null;

  private onOffline: (() => void) | null = null;

  private readonly listeners: ManagedListener[] = [];

  init(onOnline: () => void, onOffline: () => void): void {
    this.onOnline = onOnline;
    this.onOffline = onOffline;

    if (typeof window === 'undefined') return;

    this.online = typeof navigator === 'undefined' ? true : navigator.onLine;

    const handleOnline = (): void => {
      if (this.simulatedOffline) return;
      this.online = true;
      this.paused = false;
      this.onOnline?.();
    };

    const handleOffline = (): void => {
      if (this.simulatedOffline) return;
      this.online = false;
      this.paused = true;
      this.onOffline?.();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    this.listeners.push(
      { type: 'online', handler: handleOnline as EventListener },
      { type: 'offline', handler: handleOffline as EventListener }
    );
  }

  isOnline(): boolean {
    return this.online && !this.simulatedOffline;
  }

  isPaused(): boolean {
    return this.paused || this.simulatedOffline;
  }

  destroy(): void {
    if (typeof window !== 'undefined') {
      for (const entry of this.listeners) {
        window.removeEventListener(entry.type, entry.handler);
      }
    }
    this.listeners.length = 0;
    this.onOnline = null;
    this.onOffline = null;
  }

  simulateOffline(offline: boolean): void {
    this.simulatedOffline = offline;
    if (offline) {
      this.paused = true;
      this.onOffline?.();
    } else {
      this.paused = false;
      this.online = true;
      this.onOnline?.();
    }
  }

  getState(): OfflineDebugState {
    return {
      isOnline: this.isOnline(),
      paused: this.isPaused(),
      simulatedOffline: this.simulatedOffline,
    };
  }
}
